// /api/admin/* hooks — companies + switch-company.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth';

export interface CompanyRow {
  id: string;
  name: string;
  legalName?: string | null;
  pan?: string | null;
  cin?: string | null;
  fiscalYearStart: 'APRIL' | 'JANUARY';
  baseCurrency: string;
  // B-035 — Tally allows negative stock by default; Orenza blocks it unless a
  // company opts in here.
  allowNegativeStock?: boolean;
  logoUrl?: string | null;
  address?: {
    line1?: string;
    line2?: string | null;
    city?: string;
    state?: string;
    stateCode?: string;
    pincode?: string;
    country?: string;
  } | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  websiteUrl?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export interface CompanyCreateInput {
  name: string;
  legalName?: string;
  pan?: string;
  gstin?: string; // creates the Head Office branch with this GSTIN
  fiscalYearStart?: 'APRIL' | 'JANUARY';
  baseCurrency?: string;
  allowNegativeStock?: boolean;
  contactEmail?: string;
  contactPhone?: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    stateCode: string;
    pincode: string;
    country?: string;
  };
}

export function useCompanies() {
  return useQuery({
    queryKey: ['admin', 'companies'],
    queryFn: async () => {
      const res = await api.get<{ rows: CompanyRow[]; pagination: unknown }>('/admin/companies', {
        params: { pageSize: 100 },
      });
      return res.data;
    },
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CompanyCreateInput) => {
      const res = await api.post<CompanyRow>('/admin/companies', input);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'companies'] });
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

// Archive (soft-delete) a company. The backend re-authenticates the caller with
// their own password, so the mutation carries it in the body.
export function useDeleteCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, password }: { id: string; password: string }) => {
      const res = await api.delete<{ ok: boolean; id: string }>(`/admin/companies/${encodeURIComponent(id)}`, {
        data: { password },
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'companies'] });
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

interface SwitchCompanyResponse {
  companyId: string;
  activeCompanyId: string;
  activeBranchId: string | null;
  accessToken: string;
  accessTokenExpiresInMs: number;
}

export function useSwitchCompany() {
  const qc = useQueryClient();
  const setActiveCompany = useAuthStore((s) => s.setActiveCompany);
  return useMutation({
    mutationFn: async (companyId: string) => {
      const res = await api.post<SwitchCompanyResponse>('/admin/switch-company', { companyId });
      return res.data;
    },
    onSuccess: (data) => {
      // The new access cookie is already set server-side — the next API call will use it.
      // Update local auth store so company-scoped UI re-renders immediately.
      setActiveCompany(data.activeCompanyId, data.activeBranchId);
      // Invalidate every cache that depends on activeCompany scope.
      qc.invalidateQueries();
    },
  });
}
